import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePartCodeCategories } from '@/hooks/usePSI';
import { ICategoryInfo } from '@/types/psi';
import PartCodeDialog from './PartCodeDialog';
import BOMDialog from './BOMDialog';

// Category icons for visual distinction
const CATEGORY_ICONS: Record<string, string> = {
  electronic_parts: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z',
  electronic_modules: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
  dev_kits: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
  electronic_products: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
  plastic_parts: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  metal_parts: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  optical_parts: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  optical_modules: 'M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z',
  screws: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',
  consumables: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4',
  equipment: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
  production: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z',
};

// Category colors for visual distinction
const CATEGORY_COLORS: Record<string, { bg: string; hover: string; border: string; text: string }> = {
  electronic_parts: { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', border: 'border-blue-600', text: 'text-blue-600' },
  electronic_modules: { bg: 'bg-indigo-500', hover: 'hover:bg-indigo-600', border: 'border-indigo-600', text: 'text-indigo-600' },
  dev_kits: { bg: 'bg-purple-500', hover: 'hover:bg-purple-600', border: 'border-purple-600', text: 'text-purple-600' },
  electronic_products: { bg: 'bg-pink-500', hover: 'hover:bg-pink-600', border: 'border-pink-600', text: 'text-pink-600' },
  plastic_parts: { bg: 'bg-green-500', hover: 'hover:bg-green-600', border: 'border-green-600', text: 'text-green-600' },
  metal_parts: { bg: 'bg-gray-500', hover: 'hover:bg-gray-600', border: 'border-gray-600', text: 'text-gray-600 dark:text-gray-400' },
  optical_parts: { bg: 'bg-cyan-500', hover: 'hover:bg-cyan-600', border: 'border-cyan-600', text: 'text-cyan-600' },
  optical_modules: { bg: 'bg-teal-500', hover: 'hover:bg-teal-600', border: 'border-teal-600', text: 'text-teal-600' },
  screws: { bg: 'bg-orange-500', hover: 'hover:bg-orange-600', border: 'border-orange-600', text: 'text-orange-600' },
  consumables: { bg: 'bg-yellow-500', hover: 'hover:bg-yellow-600', border: 'border-yellow-600', text: 'text-yellow-600' },
  equipment: { bg: 'bg-red-500', hover: 'hover:bg-red-600', border: 'border-red-600', text: 'text-red-600' },
  production: { bg: 'bg-emerald-500', hover: 'hover:bg-emerald-600', border: 'border-emerald-600', text: 'text-emerald-600' },
};

const PartCodeSelector: React.FC = () => {
  const { data: categories, isLoading, error } = usePartCodeCategories();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startAngle, setStartAngle] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<ICategoryInfo | null>(null);
  const [showBOMDialog, setShowBOMDialog] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Restore selected category from URL query string on mount
  useEffect(() => {
    const categoryId = searchParams.get('category');
    if (categoryId && categories && categories.length > 0) {
      const category = categories.find(c => c.id === categoryId);
      if (category) {
        setSelectedCategory(category);
      }
    }
  }, [categories, searchParams]);

  // Calculate angle for each category (12 categories = 30 degrees each)
  const getAngleForIndex = (index: number, total: number) => {
    return (360 / total) * index - 90; // Start from top
  };

  // Calculate position on circle
  const getPositionOnCircle = (angle: number, radius: number) => {
    const radian = (angle * Math.PI) / 180;
    return {
      x: Math.cos(radian) * radius,
      y: Math.sin(radian) * radius,
    };
  };

  // Handle mouse/touch events for rotation
  const getAngleFromEvent = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!containerRef.current) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    return Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setStartAngle(getAngleFromEvent(e) - rotation);
  }, [getAngleFromEvent, rotation]);

  const handleMouseMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const currentAngle = getAngleFromEvent(e);
    setRotation(currentAngle - startAngle);
  }, [isDragging, getAngleFromEvent, startAngle]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse up listener
  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, []);

  const handleCategoryClick = (category: ICategoryInfo) => {
    if (!isDragging) {
      setSelectedCategory(category);
      // Update URL query string with selected category
      setSearchParams({ category: category.id });
    }
  };

  const handleCloseDialog = () => {
    setSelectedCategory(null);
    // Remove category from URL query string
    setSearchParams({});
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 py-8">
        Failed to load categories
      </div>
    );
  }

  const categoryList = categories || [];

  // Responsive dimensions
  const containerSize = isMobile ? 300 : 500;
  const radius = isMobile ? 110 : 180;
  const centerSize = isMobile ? 'w-20 h-20' : 'w-32 h-32';
  const buttonSize = isMobile ? 'w-14 h-14' : 'w-20 h-20';
  const iconSize = isMobile ? 'w-4 h-4' : 'w-6 h-6';
  const outerRingRadius = isMobile ? 140 : 240;

  return (
    <div className="space-y-6 overflow-hidden">
      {/* Instructions */}
      <div className="text-center text-gray-600 dark:text-gray-400 text-sm">
        <p>Drag to rotate the wheel. Click on a category to view and edit part codes.</p>
      </div>

      {/* Main Content: Part Codes Wheel + BOM Circle - positioned to the right */}
      <div className="flex flex-col lg:flex-row items-center justify-end gap-8 lg:gap-16 overflow-hidden">
        {/* Rotating Wheel */}
        <div
          ref={containerRef}
          className="relative select-none flex-shrink-0"
          style={{ width: `${containerSize}px`, height: `${containerSize}px` }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
        >
          {/* Center circle */}
          <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${centerSize} bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full shadow-lg flex items-center justify-center z-10`}>
            <div className="text-center text-white">
              <svg className={`${isMobile ? 'w-6 h-6' : 'w-10 h-10'} mx-auto mb-1`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <span className={`${isMobile ? 'text-[10px]' : 'text-sm'} font-medium`}>Part Codes</span>
            </div>
          </div>

          {/* Categories around the circle */}
          <div
            className="absolute inset-0 transition-transform"
            style={{
              transform: `rotate(${rotation}deg)`,
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          >
            {categoryList.map((category, index) => {
              const angle = getAngleForIndex(index, categoryList.length);
              const pos = getPositionOnCircle(angle, radius);
              const colors = CATEGORY_COLORS[category.id] || CATEGORY_COLORS.electronic_parts;
              const iconPath = CATEGORY_ICONS[category.id] || CATEGORY_ICONS.electronic_parts;

              return (
                <div
                  key={category.id}
                  className="absolute"
                  style={{
                    left: `calc(50% + ${pos.x}px)`,
                    top: `calc(50% + ${pos.y}px)`,
                    transform: `translate(-50%, -50%) rotate(${-rotation}deg)`,
                  }}
                >
                  <button
                    onClick={() => handleCategoryClick(category)}
                    className={`group flex flex-col items-center justify-center ${buttonSize} ${colors.bg} ${colors.hover} rounded-full shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg`}
                    title={category.name}
                  >
                    <svg
                      className={`${iconSize} text-white`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={iconPath}
                      />
                    </svg>
                    <span className={`${isMobile ? 'text-[8px]' : 'text-xs'} text-white font-medium mt-1 max-w-full truncate px-1`}>
                      {category.name}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Decorative outer ring */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            <circle
              cx={containerSize / 2}
              cy={containerSize / 2}
              r={outerRingRadius}
              fill="none"
              stroke="url(#gradient)"
              strokeWidth="2"
              strokeDasharray="10 5"
              opacity="0.3"
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6366f1" />
                <stop offset="100%" stopColor="#8b5cf6" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* BOM Circle */}
        <div className="flex flex-col items-center gap-4">
          <button
            onClick={() => setShowBOMDialog(true)}
            className={`group relative ${isMobile ? 'w-32 h-32' : 'w-48 h-48'} bg-gradient-to-br from-amber-500 to-orange-600 rounded-full shadow-xl flex flex-col items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-2xl`}
          >
            {/* Decorative ring */}
            <div className="absolute inset-0 rounded-full border-4 border-amber-300/30 animate-pulse" />
            <div className="absolute inset-2 rounded-full border-2 border-white/20" />

            {/* BOM Icon */}
            <svg className={`${isMobile ? 'w-10 h-10' : 'w-16 h-16'} text-white mb-2`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <span className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-white`}>{isMobile ? 'BOM' : 'Bill of Materials'}</span>
            {!isMobile && <span className="text-sm text-white/80 mt-1">BOM</span>}

            {/* Hover indicator */}
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
              <svg className="w-6 h-6 text-amber-600 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {/* BOM Subcategory indicators */}
          <div className="flex gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 rounded-full border border-indigo-200">
              <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
              <span className="text-xs font-medium text-indigo-600">Electronic</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-200">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <span className="text-xs font-medium text-emerald-600">Structural</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 rounded-full border border-amber-200">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <span className="text-xs font-medium text-amber-600">Products</span>
            </div>
          </div>
        </div>
      </div>

      {/* Category Legend */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 max-w-3xl mx-auto px-2">
        {categoryList.map((category) => {
          const colors = CATEGORY_COLORS[category.id] || CATEGORY_COLORS.electronic_parts;
          return (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category)}
              className={`flex items-center gap-2 p-2 rounded-lg border-2 ${colors.border} bg-white dark:bg-gray-800 hover:bg-gray-50 transition-colors`}
            >
              <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
              <span className={`text-sm font-medium ${colors.text}`}>{category.name}</span>
            </button>
          );
        })}
      </div>

      {/* Part Code Dialog */}
      {selectedCategory && (
        <PartCodeDialog
          category={selectedCategory}
          onClose={handleCloseDialog}
        />
      )}

      {/* BOM Dialog */}
      {showBOMDialog && (
        <BOMDialog onClose={() => setShowBOMDialog(false)} />
      )}
    </div>
  );
};

export default PartCodeSelector;
